/**
 * Die Vorlese-Karte für eine Textdatei (Werkzeug abspielen, ziel datei). (19.10.2026)
 *
 * Wörtliches Vorlesen eines Dokuments läuft nie über die Antwort, sondern über den
 * Player: Das Werkzeug liefert nur file_url (und ab_zeichen) — KEINEN Inhalt. Die Karte
 * holt den Text selbst aus /uploads, zerlegt ihn wie der Server (shared/doc-narration.ts,
 * dieselben Abschnitte = dieselben Zwischenspeicher-Schlüssel der Hintergrund-Vertonung)
 * und baut den geteilten Vorlese-Player (Schieber, Zeit, Stopp, merkt die Stelle).
 *
 * ⚠️ Gestartet wird erst nach Vesperas Satz und nie beim Bau (aus dem Verlauf startet nichts).
 * ⚠️ Der Text wird nie angezeigt und nie ans Modell zurückgegeben.
 */
import { absatz, baueKarte } from "./karte.js";
import { baueTippHinweis, type Abspieler } from "./abspielen.js";
import { getViewerPlayerBuilder, type ViewerNarrationPlayer } from "./project-file-viewer.js";
import { splitDocChapters } from "../../shared/doc-narration.js";
import { auspacken } from "./schritte.js";

/** Mediathek oder eine eigene Projektdatei (kontogeprüfte /raw-Route). */
export const istVorleseUrl = (u: string): boolean =>
  u.startsWith("/uploads/") || /^\/api\/projects\/[\w-]+\/files\/[\w-]+\/raw$/.test(u);

const text = (v: unknown): string => (typeof v === "string" ? v : "");

/** Gleiche Zerlegung wie der Server (tools/abspielen.ts): ab Zeichenstelle, dann Abschnitte. */
export function dateiAbschnitte(roh: string, abZeichen: number) {
  const ab = Number.isFinite(abZeichen) && abZeichen > 0 ? Math.floor(abZeichen) : 0;
  return splitDocChapters(roh.slice(ab)).filter((a) => a.text.trim());
}

export function baueDateiVorleseKarte(result: unknown, onAbspieler?: (a: Abspieler) => void): HTMLElement {
  const r = auspacken(result);
  const titel = text(r.titel) || "Datei";
  const url = text(r.url);
  const ab = Number(r.ab_zeichen) || 0;
  const karte = baueKarte("abspielen", titel);
  karte.classList.add("tool-datei-vorlesen");
  const ort = document.createElement("div");
  ort.className = "datei-vorlese-spieler";
  karte.appendChild(ort);

  let player: ViewerNarrationPlayer | null = null;
  const bereit: Promise<void> = (async () => {
    const baue = getViewerPlayerBuilder();
    if (!baue || !istVorleseUrl(url)) throw new Error("kein Player");
    const antwort = await fetch(url, { credentials: "same-origin" });
    if (!antwort.ok) throw new Error(`HTTP ${antwort.status}`);
    const roh = await antwort.text();
    const abschnitte = dateiAbschnitte(roh, ab);
    if (abschnitte.length === 0) throw new Error("leer");
    const p = baue({
      // Kein eigener Server-Weg: mit nicht-leerem Text geht der Player über /api/tts/prepare (Abschnitte sind nie leer).
      endpoint: "",
      getBody: (voiceId) => ({ text: abschnitte[0]?.text ?? "", voiceId }),
      sectionable: true,
      resumeId: `datei:${url}:${ab}`,
      resumeVersion: String(roh.length),
    });
    p.setSections(abschnitte);
    p.setReady(true);
    ort.appendChild(p.el);
    player = p;
  })().catch(() => {
    // Ehrlich statt still: ohne Player wird nichts vorgelesen.
    ort.appendChild(absatz("Der Vorlese-Player ließ sich nicht aufbauen — die Datei wurde nicht vorgelesen.", "hint"));
  });

  onAbspieler?.({
    spielen: () =>
      void bereit.then(() => {
        if (karte.isConnected && player && !player.aktiv?.()) player.play?.();
      }),
    anbieten: () =>
      void bereit.then(() => {
        if (karte.isConnected && player) ort.before(baueTippHinweis());
      }),
  });
  return karte;
}
